import { useSyncExternalStore } from 'react';
import { RefreshCw } from 'lucide-react';
import { applyUpdateNow, isUpdatePending, subscribePwaUpdate } from './pwaUpdate';
import { isIntakePath } from './publicIntakeScope';
import { useHasUnsavedChanges } from '../guards/unsavedChanges';

/*
  Shown only while a new service worker is waiting AND the user has unsaved edits.
  A clean update needs no toast — it applies silently at the next navigation (see
  pwaUpdate.ts). The toast is the affordance for the dirty case: it tells the user
  an update is queued, and "Reload now" applies it immediately (dropping the
  in-progress edits). Otherwise the update applies on their next clean navigation.
*/
export function UpdateToast() {
  const pending = useSyncExternalStore(subscribePwaUpdate, isUpdatePending, isUpdatePending);
  const isDirty = useHasUnsavedChanges();
  if (!pending || !isDirty) return null;
  // Never on the public intake form (#721). The copy below promises the update will
  // apply "when you save or move on", and on that route neither happens: there is no
  // save and no navigation, which is the whole premise of the detection-time rule in
  // updatePolicy.ts. A dirty form there means a client is mid-answer, so the honest
  // behaviour is to say nothing and let the update apply on their next visit — this
  // is a prospective client of the firm, not an operator who needs to know a build
  // shipped. Read straight off `location` because this component sits outside
  // RouterProvider (main.tsx); the route is only ever entered by a full page load, so
  // there is no soft navigation for the read to go stale against.
  if (isIntakePath(window.location.pathname)) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-5 left-1/2 z-50 -translate-x-1/2 flex items-center gap-4 rounded-token border border-line bg-surface px-5 py-3.5 shadow-[0_12px_40px_-12px_rgba(15,23,42,0.3)]"
    >
      <RefreshCw size={16} strokeWidth={1.75} aria-hidden className="text-bronze" />
      <span className="font-sans text-[13px] text-navy">
        A new version is ready. It will apply when you save or move on.
      </span>
      <button
        onClick={() => applyUpdateNow()}
        className="shrink-0 rounded-token border-none bg-navy px-4 py-1.5 font-sans text-[11px] font-bold uppercase tracking-[0.1em] text-cream cursor-pointer"
      >
        Reload now
      </button>
    </div>
  );
}
